import {ChangeDetectionStrategy, Component, computed, effect, Inject, Injectable, InjectionToken, input, Signal, signal, viewChildren} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {Observable, switchMap} from "rxjs";
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop";
import {DokumentKortvisningKomponent} from "../../../dokument/komponenter/dokument-kortvisning/dokument-kortvisning";
import {DokumentListevisningKomponent} from "../../../dokument/komponenter/dokument-listevisning/dokument-listevisning";
import {DokumentOpplastingKortvisningKomponent} from "../../../dokument/komponenter/dokument-opplasting-kortvisning/dokument-opplasting-kortvisning";
import {DokumentOpplastingListevisningKomponent} from "../../../dokument/komponenter/dokument-opplasting-listevisning/dokument-opplasting-listevisning";
import {MatIcon} from "@angular/material/icon";
import {MatButton} from "@angular/material/button";
import {TranslateModule} from "@ngx-translate/core";
import {ReactiveFormsModule} from "@angular/forms";
import {DokumentvisningBasisKomponent} from "../../../dokument/komponenter/dokumentvisning/DokumentvisningBasisKomponent";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {erLikeLister} from "felles-ng2/støttefunksjoner/lister";
import {AgendaelementId, DokumentId, GruppeId, SakRessursClient, SaksdokumentModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {SaksdokumentDatatjeneste} from "felles-ng2/tjenester/datalager/SaksdokumentDatatjeneste";
import {Appkonfigurasjon} from "felles-ng2/tjenester/Appkonfigurasjon";
import {fadeInnUtAnimasjon} from "web-ng2/delt/animasjoner";
import {CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray} from "@angular/cdk/drag-drop";
import {NgTemplateOutlet} from "@angular/common";
import {ValgtDokumentRegister} from "web-ng2/moduler/sak/tjenester/ValgtDokumentRegister";
import {Opplasting} from "felles-ng2/tjenester/filopplasting/SekvensiellFilopplasting";
import {NavigasjonforhindretFlashDirektiv} from "web-ng2/delt/direktiver/navigasjonforhindret-flash/navigasjonforhindret-flash";
import {NavigasjonDeaktivering} from "web-ng2/tjenester/NavigasjonDeaktivering";
import {EnkeltklikkDirektiv} from "web-ng2/delt/direktiver/enkeltklikk/enkeltklikk";

const debug = initDebugger('SakListeDokumenterKomponent');

/**  InjectionToken for injisering av {@link SaksdokumenterUnderOpplasting} implementasjon. */
export const SaksdokumenterUnderOpplasting_DI_TOKEN = new InjectionToken<SaksdokumenterUnderOpplasting>('SaksdokumenterUnderOpplasting implementasjon');

/**
 * Lister saksdokumenter.
 * Med støtte for å vise dokumenter i liste-form eller 'kort'-form.
 */
@Component({
    selector: 'sak-liste-dokumenter',
    templateUrl: 'sak-liste-dokumenter.html',
    styleUrl: 'sak-liste-dokumenter.scss',
    animations: [fadeInnUtAnimasjon('0.5s')],
    imports: [
        DokumentKortvisningKomponent,
        DokumentListevisningKomponent,
        DokumentOpplastingKortvisningKomponent,
        DokumentOpplastingListevisningKomponent,
        MatIcon,
        MatButton,
        TranslateModule,
        ReactiveFormsModule,
        CdkDropList,
        CdkDrag,
        MatButton,
        NgTemplateOutlet,
        NavigasjonforhindretFlashDirektiv,
        EnkeltklikkDirektiv
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SakListeDokumenterKomponent {

    sakId = input.required<AgendaelementId>();
    eiendeGruppeId = input.required<GruppeId>();
    visning = input<Visningsmodus>('kort');

    //i følge dok skulle det være mulig å angi basisklassen 'viewChildren(DokumentvisningBasisKomponent)' - men dette resulterer i en tom array
    //kanskje fordi DokumentvisningBasisKomponent er def med @Component og uttrykket derfor ser etter direkte forekomster av denne komp, og ikke komp som arver fra denne.
    private dokumentKortvisinger = viewChildren(DokumentKortvisningKomponent);
    private dokumentListevisinger = viewChildren(DokumentListevisningKomponent);

    dokumenter = signal<SaksdokumentModell[]>([]);
    sorteringEndret = computed(() => !erLikeLister(this.dokumenter().map(d => d.dokumentId), this.initiellDokumentsortering()));
    /** Dokument vi viser detaljer for i høyre felt */
    valgtDokument = signal<SaksdokumentModell | null>(null);
    opplastinger = this.saksdokumenterUnderOpplasting.ikkeFerdigeOpplastinger();

    lagrer = asynkArbeidPågårSignal();

    private initiellDokumentsortering = signal<DokumentId[]>([]);

    constructor(private avhengigheter: SakListeDokumenterKomponentAvhengigheter,
                @Inject(SaksdokumenterUnderOpplasting_DI_TOKEN) private saksdokumenterUnderOpplasting: SaksdokumenterUnderOpplasting,
                /** Tjeneste som brukes for å holde rede på valgt dokument, forventes gjort tilgjengelig av omsluttende komponent */
                private valgtDokumentRegister: ValgtDokumentRegister,
                private navigasjonDeaktivering: NavigasjonDeaktivering) {

        // Last dokumenter når sakId endres
        toObservable(this.sakId).pipe(
          switchMap(sakId => this.avhengigheter.saksdokumenter(sakId)),
          takeUntilDestroyed())
          .subscribe(dokumenter => {
              this.dokumenter.set(dokumenter);
              this.initiellDokumentsortering.set(dokumenter.map(d => d.dokumentId));
          });

        //logikk knyttet til at sortering aktiveres/deaktiveres
        effect(() => {
            this.navigasjonDeaktivering.aktiverNavigasjon(!this.sorteringEndret());
        });
    }

    finnDokumentKomponent(dokumentId: DokumentId): DokumentvisningBasisKomponent | undefined {
        return ([...this.dokumentKortvisinger(), ...this.dokumentListevisinger()] as DokumentvisningBasisKomponent[])
          .find(komponent => komponent.dokumentversjon().dokumentId == dokumentId);
    }

    /** Viser detaljer for angitt dokument i høyre-del av vindu  */
    async visDetaljerFor(dokument: SaksdokumentModell) {
        debug(`visDetaljerFor ${dokument.filnavn}`)
        this.valgtDokumentRegister.setValgt(dokument.dokumentId);
    }

    /** Kalles når dokumentsorteringen har blitt endret via dra-og-slipp */
    dokumentFlyttet(event: CdkDragDrop<SaksdokumentModell>) {
        debug('dokumentFlyttet', {fra: event.previousIndex, til: event.currentIndex}, event);
        const liste = [...this.dokumenter()];
        moveItemInArray(liste, event.previousIndex, event.currentIndex);
        this.dokumenter.set(liste);
    }

    async lagreNySortering() {
        await this.lagrer.utfør(async () => this.avhengigheter.oppdaterRekkefølge(this.sakId(), this.dokumenter()));
        this.initiellDokumentsortering.set(this.dokumenter().map(d => d.dokumentId));
    }

    avbrytNySortering() {
        this.dokumenter.set(this.initiellDokumentsortering().map(id => this.dokumenter().find(d => d.dokumentId == id)!));
    }

    /** Åpner inline visning av angitt dokument, kalles av omsluttende komponent, delegerer til {@link DokumentvisningBasisKomponent#aapneInline}.
     * @return false dersom angitt dokument ikke finnes */
    åpneDokumentInline(saksdokument: SaksdokumentModell): boolean {
        debug(`åpneDokumentInline ${saksdokument.filnavn}`);
        const dokumentKomp = this.finnDokumentKomponent(saksdokument.dokumentId);
        if (erNil(dokumentKomp)) return false;
        else dokumentKomp.aapneInline();
        return true;
    }

    //hjelpemetoder

}

export type Visningsmodus = 'liste' | 'kort';

/** Gir tilgang til dokumenter som er under opplasting. Forventes gjort tilgjengelig under DI nøkkelen {@link SaksdokumenterUnderOpplasting_DI_TOKEN}  */
export interface SaksdokumenterUnderOpplasting {
    ikkeFerdigeOpplastinger(): Signal<Opplasting<SaksdokumentModell>[]>;
}


@Injectable()
export class SakListeDokumenterKomponentAvhengigheter {

    constructor(private saksdokumentDatatjeneste: SaksdokumentDatatjeneste, private sakRessurs: SakRessursClient, private konfigurasjon: Appkonfigurasjon) {
    }

    saksdokumenter(sakId: AgendaelementId): Observable<SaksdokumentModell[]> {
        return this.saksdokumentDatatjeneste.finnTilknyttetSakFraLokallager(sakId);
    }

    async oppdaterRekkefølge(sakId: AgendaelementId, oppdatertRekkefølge: SaksdokumentModell[]): Promise<void> {
        await this.sakRessurs.oppdaterDokumentrekkefølge(sakId, {dokumentrekkefoelge: oppdatertRekkefølge.map(d => d.dokumentId)}).somPromise();
    }
}

